import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { Client, ClientSelection } from '../types/users';
import { useAuth } from './AuthContext';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { toBooleanFlag } from '../utils/featureFlags';
// Multi-tab fix (mayo 2026): el cliente activo se persiste por-tab en
// sessionStorage. Ver src/utils/clientStorage.ts y docs/architecture/PLAN_URL_ROUTING.md
import {
  getStoredSelectedClientRaw,
  setStoredSelectedClientRaw,
  removeStoredSelectedClient,
} from '../utils/clientStorage';

interface ClientContextType {
  selectedClient: ClientSelection | null;
  setSelectedClient: (client: ClientSelection | null) => void;
  availableClients: Client[];
  setAvailableClients: (clients: Client[]) => void;
  isLoadingClients: boolean;
  setIsLoadingClients: (loading: boolean) => void;
  isInAdminMode: boolean;
  setIsInAdminMode: (inAdminMode: boolean) => void;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const ClientProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { userData } = useAuth();
  const [selectedClient, setSelectedClientInternal] = useState<ClientSelection | null>(null);
  const [isInAdminMode, setIsInAdminMode] = useState(false);
  
  const setSelectedClient = useCallback((client: ClientSelection | null) => {
    setSelectedClientInternal(client);
    // Set admin mode flag when selecting admin-mode
    if (client && client.clientId === 'admin-mode') {
      setIsInAdminMode(true);
    } else {
      // Clear admin mode when selecting a real client or clearing selection
      setIsInAdminMode(false);
    }
  }, []);
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  // Clear selected client when user logs out
  useEffect(() => {
    if (!userData) {
      setSelectedClient(null);
      setAvailableClients([]);
      setIsInAdminMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // Store selected client in sessionStorage (per-tab) for persistence.
  // sessionStorage en lugar de localStorage para que dos tabs puedan tener
  // distintos clientes activos sin pisarse (multi-tab fix mayo 2026).
  useEffect(() => {
    if (selectedClient) {
      setStoredSelectedClientRaw(JSON.stringify(selectedClient));
    } else {
      removeStoredSelectedClient();
    }
  }, [selectedClient]);

  // Load selected client from sessionStorage on mount
  useEffect(() => {
    if (!userData) {
      setIsLoadingClients(false);
      return;
    }

    const loadStoredClient = async () => {
      setIsLoadingClients(true);
      const stored = getStoredSelectedClientRaw();

      if (stored) {
        try {
          const parsedClient = JSON.parse(stored);
          const normalizedClient = {
            ...parsedClient,
            enable_tags: toBooleanFlag(parsedClient.enable_tags, true),
            enable_sku: toBooleanFlag(parsedClient.enable_sku, false)
          };

          // If it's admin mode, just restore it
          if (normalizedClient.clientId === 'admin-mode') {
            setSelectedClient(normalizedClient);
            setIsInAdminMode(true);
            setIsLoadingClients(false);
            return;
          }

          // For regular clients, fetch the full client data including logo
          try {
            const db = getFirestore();
            const clientDoc = await getDoc(doc(db, 'clients', normalizedClient.clientId));

            if (clientDoc.exists()) {
              const clientData = clientDoc.data();

              const fullClientData = {
                clientId: normalizedClient.clientId,
                clientName: clientData.name || normalizedClient.clientName,
                clientLogo: clientData.logo,
                clientLogoHeight: clientData.logoHeight,
                clientType: clientData.client_type,
                clientWelcomeVideo: clientData.welcomeVideo,
                enable_tags: toBooleanFlag(clientData.enable_tags, true),
                enable_sku: toBooleanFlag(clientData.enable_sku, false)
              };

              setSelectedClient(fullClientData);
              // Update sessionStorage with full data (per-tab persistence)
              setStoredSelectedClientRaw(JSON.stringify(fullClientData));
            } else {
              // Client doesn't exist anymore, use stored data
              setSelectedClient(normalizedClient);
            }
          } catch (error) {
            console.error('❌ [ClientContext] Error fetching client data:', error);
            // Fall back to stored data
            setSelectedClient(normalizedClient);
          }
        } catch (e) {
          console.error('❌ [ClientContext] Error loading selected client:', e);
          removeStoredSelectedClient();
        }
      }

      setIsLoadingClients(false);
    };
    
    loadStoredClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // PT-#87 PR-d: override de config SOLO en development (LOCAL_CLIENT_OVERRIDES del backend
  // local). enable_tags se lee de Firestore en el front; el backend expone el bloque `client`
  // del JSON local por un endpoint dev-only. En el build de producción este bloque es código
  // muerto (NODE_ENV='production') y el endpoint no existe.
  const selectedClientId = selectedClient?.clientId;
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (!selectedClientId || selectedClientId === 'admin-mode') return;
    let cancelled = false;
    const base = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    fetch(`${base}/api/dev/client-overrides/${encodeURIComponent(selectedClientId)}`)
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled || !data || !data.active || !data.client) return;
        console.warn(`[ClientContext] LOCAL_CLIENT_OVERRIDES activo para ${selectedClientId}:`, data.client);
        if (data.client.enable_tags === undefined) return;
        const enableTags = toBooleanFlag(data.client.enable_tags, true);
        setSelectedClientInternal(prev => (
          prev && prev.clientId === selectedClientId && prev.enable_tags !== enableTags
            ? { ...prev, enable_tags: enableTags }
            : prev
        ));
      })
      .catch(() => { /* backend sin endpoint dev: sin override */ });
    return () => { cancelled = true; };
  }, [selectedClientId]);

  const value = useMemo<ClientContextType>(() => ({
    selectedClient,
    setSelectedClient,
    availableClients,
    setAvailableClients,
    isLoadingClients,
    setIsLoadingClients,
    isInAdminMode,
    setIsInAdminMode,
  }), [selectedClient, setSelectedClient, availableClients, isLoadingClients, isInAdminMode]);

  return (
    <ClientContext.Provider value={value}>
      {isLoadingClients ? (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando...</p>
          </div>
        </div>
      ) : children}
    </ClientContext.Provider>
  );
};

export const useClient = () => {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};
